import { createSlice } from '@reduxjs/toolkit';
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';

import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import createFormSlice from '@/store/reducers/createFormSlice';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { getTranslation } from '@/core/utils/i18nHelper';
import {
	toProfileSettingsPayload,
} from '@/store/types/settingsFormTypes';
import type { ProfileFormValues } from '@/components/features/settings/types';
import type { RootState } from '@/store/store';

type ProfileThunk<R = void> = ThunkAction<Promise<R>, RootState, unknown, UnknownAction>;

/**
 * Дефолты формы профиля. Это «пусто» — реальные значения приезжают
 * из API через `loadProfileSettings` и кладутся в `data` через
 * `setProfileSettingsData`.
 */
const initialProfileData: ProfileFormValues = {
	gender: '',
	name: '',
	dobDay: '',
	dobMonth: '',
	dobYear: '',
};

// `createFormSlice` по умолчанию выставляет `isFetching: true` — удобно
// для форм, которые ждут загрузку начальных данных. Нам форма рендерится
// сразу с пустыми дефолтами, поэтому стартуем с `false`, иначе кнопка
// «Сохранить» окажется заблокированной до первого запроса.
const formSlice = createFormSlice('profileSettings', initialProfileData, { isFetching: false });

const combinedSlice = createSlice({
	name: 'profileSettings',
	initialState: {
		...formSlice.getInitialState(),
		// Жизненный цикл начальной загрузки профиля (GET) держим ОТДЕЛЬНО от
		// `isFetching`/`error`, которые принадлежат форме настроек (PATCH).
		// Иначе спиннер шапки заблокировал бы кнопку «Сохранить», а сетевая
		// ошибка загрузки протекла бы в форму как ошибка сохранения.
		profileLoading: true,
		profileLoadError: false,
	},
	reducers: {
		setProfileSettingsData(state, action) {
			state.data = { ...state.data, ...action.payload };
		},
		profileLoadStarted(state) {
			state.profileLoading = true;
			state.profileLoadError = false;
		},
		profileLoadSucceeded(state) {
			state.profileLoading = false;
			state.profileLoadError = false;
		},
		profileLoadFailed(state) {
			state.profileLoading = false;
			state.profileLoadError = true;
		},
		...formSlice.caseReducers,
	},
});

export const {
	resetSuccessMessage,
	setError,
	setProfileSettingsData,
} = combinedSlice.actions;

/**
 * Загружает текущие настройки профиля через GET /api/profile/settings
 * и раскладывает дату рождения на три селекта формы (dobDay/dobMonth/dobYear).
 *
 * Диспатчит `setProfileSettingsData(...)` — `data` слайса остаётся
 * единой точкой правды для имени пользователя во всём UI.
 */
export const loadProfileSettings = (): ProfileThunk => async (dispatch) => {
	dispatch(combinedSlice.actions.profileLoadStarted());

	try {
		const { data } = await apiUrl.get<{ gender: string | null; name: string; dob: string | null }>(
			API_ENDPOINTS.profileSettings,
		);

		const [dobYear = '', dobMonth = '', dobDay = ''] = data.dob ? data.dob.split('-') : [];

		dispatch(setProfileSettingsData({
			gender: data.gender ?? '',
			name: data.name,
			dobYear,
			dobMonth: dobMonth ? String(parseInt(dobMonth, 10)) : '',
			dobDay: dobDay ? String(parseInt(dobDay, 10)) : '',
		}));
		dispatch(combinedSlice.actions.profileLoadSucceeded());
	} catch {
		dispatch(combinedSlice.actions.profileLoadFailed());
	}
};

/**
 * Сохранение формы профиля через PATCH /api/profile/settings.
 * Маппинг UI → API выполняется одним местом (`toProfileSettingsPayload`),
 * чтобы при смене контракта правку делать только в `settingsFormTypes.ts`.
 *
 * Оптимистичное обновление `data`: до сетевого запроса кладём значения
 * формы в стор, чтобы зависимые экраны (например, шапка Profile,
 * отображающая имя) увидели новое значение сразу после клика «Сохранить».
 */
export const saveProfileSettings = (values: ProfileFormValues): ProfileThunk => async (dispatch) => {
	dispatch(setProfileSettingsData(values));

	const successMessage = getTranslation('settings.profile.success_message') as string;

	try {
		await apiUrl.patch(
			API_ENDPOINTS.profileSettings,
			toProfileSettingsPayload(values),
			{ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } },
		);
		dispatch(combinedSlice.actions.formSent(successMessage));
	} catch (error) {
		handleResponseError(error, (errorMessage: string) => dispatch(combinedSlice.actions.setError(errorMessage)));
	}
};

export default combinedSlice.reducer;
export { combinedSlice as profileSettingsSlice };
